// Client-side mirror of the server's project cost + milestone maths (server/src/modules/projects/
// projectCosts.ts + projects.ts generateMilestones), so the Costs tab shows the totals, the margin
// and the payment schedule LIVE as the office types, before saving. The server remains
// authoritative on save; these are pence-safe and kept in lock-step with the server (a shared test
// pins the same anchors). Pure -> unit tested.
import type { ProjectLabourLine, ProjectSupplierCostLine, ProjectMilestone, ProjectMilestoneKey } from "../../lib/api";

function sumPence(values: ReadonlyArray<number>): number {
  const pence = values.reduce((acc, n) => acc + (Number.isFinite(n) ? Math.round(n * 100) : 0), 0);
  return pence / 100;
}

// day_rate -> days * day rate; fixed -> the fixed amount. Negative / non-finite treated as 0.
export function labourLineCost(l: { basis: "fixed" | "day_rate"; quantity: number; unitCost: number }): number {
  const unit = Number.isFinite(l.unitCost) && l.unitCost > 0 ? l.unitCost : 0;
  if (l.basis === "day_rate") {
    const qty = Number.isFinite(l.quantity) && l.quantity > 0 ? l.quantity : 0;
    return Math.round(qty * unit * 100) / 100;
  }
  return Math.round(unit * 100) / 100;
}

export function sumLabour(labour: ReadonlyArray<ProjectLabourLine>): number {
  return sumPence(labour.map(labourLineCost));
}
export function sumSuppliers(suppliers: ReadonlyArray<{ cost: number }>): number {
  return sumPence(suppliers.map((s) => (Number.isFinite(s.cost) && s.cost > 0 ? s.cost : 0)));
}
export function sumSubcontractorCosts(costs: ReadonlyArray<number | null | undefined>): number {
  return sumPence(costs.map((c) => (c != null && Number.isFinite(c) && c > 0 ? c : 0)));
}

// ---- charges (cost + markup = charge; Session AAA Item 3, mirrors server projectCosts.ts) ----

export const DEFAULT_MARKUP_PERCENT = 30;

export function effectiveMarkupPercent(pct: number | null | undefined): number {
  if (pct == null || !Number.isFinite(pct)) return DEFAULT_MARKUP_PERCENT;
  return pct < 0 ? 0 : pct;
}

// The client charge for a cost at a markup percent: cost x (1 + markup/100), pence-exact.
export function chargeFor(cost: number, markupPercent: number | null | undefined): number {
  const costPence = Number.isFinite(cost) && cost > 0 ? Math.round(cost * 100) : 0;
  return Math.round(costPence * (1 + effectiveMarkupPercent(markupPercent) / 100)) / 100;
}

export function labourLineCharge(l: ProjectLabourLine): number {
  return chargeFor(labourLineCost(l), l.markupPercent);
}
export function supplierLineCharge(s: ProjectSupplierCostLine): number {
  return chargeFor(s.cost, s.markupPercent);
}

// The computed PROJECT VALUE (ex VAT): every line's charge summed, with the overall margin percent
// on top. Null when there are no charges (no priced work yet). Mirrors the server exactly.
export function computeProjectValueFromCosts(input: {
  labour: ReadonlyArray<ProjectLabourLine>;
  suppliers: ReadonlyArray<ProjectSupplierCostLine>;
  subcontractors: ReadonlyArray<{ id: string; cost: number | null }>;
  subcontractorMarkups?: Record<string, number>;
  overallMarginPercent?: number;
}): number | null {
  const charges = [
    ...input.labour.map(labourLineCharge),
    ...input.suppliers.map(supplierLineCharge),
    ...input.subcontractors.map((s) => chargeFor(s.cost ?? 0, input.subcontractorMarkups?.[s.id])),
  ];
  const totalChargePence = Math.round(sumPence(charges) * 100);
  if (totalChargePence <= 0) return null;
  const overall = effectiveMarkupPercent(input.overallMarginPercent ?? 0);
  return Math.round(totalChargePence * (1 + overall / 100)) / 100;
}

export interface ClientCostSummary {
  labourCost: number;
  supplierCost: number;
  subcontractorCost: number;
  totalCostToEle: number;
  projectValue: number | null;
  margin: number | null;
  marginPct: number | null;
}

// margin = projectValue - (labour + supplier + subcontractor). Mirrors server computeCostSummary.
export function computeClientCostSummary(input: {
  labour: ReadonlyArray<ProjectLabourLine>;
  suppliers: ReadonlyArray<ProjectSupplierCostLine>;
  subcontractorCosts: ReadonlyArray<number | null | undefined>;
  projectValue: number | null;
}): ClientCostSummary {
  const labourCost = sumLabour(input.labour);
  const supplierCost = sumSuppliers(input.suppliers);
  const subcontractorCost = sumSubcontractorCosts(input.subcontractorCosts);
  const totalCostToEle = sumPence([labourCost, supplierCost, subcontractorCost]);
  const projectValue = input.projectValue != null && Number.isFinite(input.projectValue) ? input.projectValue : null;
  if (projectValue == null) {
    return { labourCost, supplierCost, subcontractorCost, totalCostToEle, projectValue: null, margin: null, marginPct: null };
  }
  const margin = Math.round((projectValue - totalCostToEle) * 100) / 100;
  const marginPct = projectValue > 0 ? Math.round((margin / projectValue) * 1000) / 10 : null;
  return { labourCost, supplierCost, subcontractorCost, totalCostToEle, projectValue, margin, marginPct };
}

// The third-party element = supplier + subcontractor cost (mirrors server deriveThirdParty).
export function deriveThirdParty(input: { supplierCost: number; subcontractorCost: number }): { hasThirdParty: boolean; thirdPartyCostPence: number } {
  const total = sumPence([input.supplierCost, input.subcontractorCost]);
  const thirdPartyCostPence = Math.round(total * 100);
  return { hasThirdParty: thirdPartyCostPence > 0, thirdPartyCostPence };
}

// GBP 5,000 ex VAT, in pence. At or below this the whole order is payable with order.
const MILESTONE_THRESHOLD_PENCE = 500_000;
const MILESTONE_LABELS: Record<ProjectMilestoneKey, string> = {
  with_order: "Payment with order",
  third_party_with_order: "Third-party element with order",
  // House rule: the word "practical" is banned from customer-facing documents (Session AY).
  balance_on_completion: "Balance on completion",
};
const MILESTONE_ORDER: ProjectMilestoneKey[] = ["with_order", "third_party_with_order", "balance_on_completion"];

// Mirror of server generateMilestones: ELE-only is 100% with order under GBP5k or 50/50 over (on
// the WHOLE value); any third-party element is billed in full with order as a separate stage. All
// pence. The schedule is shown LIVE from the entered project value + the derived third-party cost.
export function generateMilestones(input: { totalValuePence: number; hasThirdParty: boolean; thirdPartyCostPence: number }): ProjectMilestone[] {
  const total = Math.max(0, Math.round(input.totalValuePence || 0));
  if (total <= 0) return [];
  const tp = input.hasThirdParty ? Math.min(Math.max(0, Math.round(input.thirdPartyCostPence)), total) : 0;
  const eleOnly = total - tp;
  const eleUpfront = total <= MILESTONE_THRESHOLD_PENCE ? eleOnly : Math.round(eleOnly / 2);
  const eleBalance = eleOnly - eleUpfront;
  const amounts: Record<ProjectMilestoneKey, number> = { with_order: eleUpfront, third_party_with_order: tp, balance_on_completion: eleBalance };
  return MILESTONE_ORDER.filter((k) => amounts[k] > 0).map((k) => ({ key: k, label: MILESTONE_LABELS[k], amountPence: amounts[k] }));
}

// The live payment schedule from the entered project value + the cost rows. Returns [] when no
// project value. Pure -> the Costs tab shows it without a round-trip.
export function previewMilestones(input: {
  projectValue: number | null;
  supplierCost: number;
  subcontractorCost: number;
}): ProjectMilestone[] {
  if (input.projectValue == null || !(input.projectValue > 0)) return [];
  const { hasThirdParty, thirdPartyCostPence } = deriveThirdParty({ supplierCost: input.supplierCost, subcontractorCost: input.subcontractorCost });
  return generateMilestones({ totalValuePence: Math.round(input.projectValue * 100), hasThirdParty, thirdPartyCostPence });
}
